import { lazy } from 'react'
import {
  AcademicCapIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  ChartBarSquareIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  DocumentChartBarIcon,
  HandRaisedIcon,
  MegaphoneIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline'
import type { VerticalModule } from '@lipaflow/shared'
import { SCHOOL_DEFAULT_CONTEXT } from '../context/defaultContext'

const AttendancePage = lazy(() => import('./pages/AttendancePage'))
const TimetablePage = lazy(() => import('./pages/TimetablePage'))
const ResultsPage = lazy(() => import('./pages/ResultsPage'))
const AssignmentsPage = lazy(() => import('./pages/AssignmentsPage'))
const DisciplinePage = lazy(() => import('./pages/DisciplinePage'))
const ClassroomDashboardPage = lazy(() => import('./pages/ClassroomDashboardPage'))
const CurriculumBrowserPage = lazy(() => import('./pages/CurriculumBrowserPage'))
const PlanningPage = lazy(() => import('./pages/PlanningPage'))
const GenerationHistoryPage = lazy(() => import('./pages/GenerationHistoryPage'))
const AssessmentPage = lazy(() => import('./pages/AssessmentPage'))
const KnecAlignmentPage = lazy(() => import('./pages/KnecAlignmentPage'))
const AnnouncementsPage = lazy(() => import('./pages/AnnouncementsPage'))
const FeeStructuresPage = lazy(() => import('./pages/FeeStructuresPage'))
const SchoolStructurePanel = lazy(() => import('./slots/SchoolStructurePanel'))
const InviteAssignmentFields = lazy(() => import('./slots/InviteAssignmentFields'))
const CohortPicker = lazy(() => import('./slots/CohortPicker'))
const CohortLabel = lazy(() => import('./slots/CohortPicker').then((m) => ({ default: m.CohortLabel })))
const SetupChecklist = lazy(() => import('./slots/SetupChecklist'))
const EntityListFilter = lazy(() => import('./slots/EntityListFilter'))
const EntityAttributeFields = lazy(() => import('./slots/EntityAttributeFields'))
const EntityAttributeBadge = lazy(() =>
  import('./slots/EntityAttributeFields').then((m) => ({ default: m.EntityAttributeBadge })),
)

// What a school buys sets these switches (education strategy): the school
// day comes with School Operations, the CBC tools with the CBC suite, fees
// with collections. Notices to parents come with every product.
const OPERATIONS = 'operations'
const CBC_SUITE = 'cbc_suite'
const COLLECTIONS = 'collections'

/**
 * The education vertical as an *operator* shell composes it.
 *
 * Deliberately separate from the payer `educationModule`. One vertical, two
 * audiences: a parent sees their own child's timetable, a teacher edits the
 * whole school's. They share a name and nothing else — including route paths,
 * which collide (`/attendance` means different pages), so merging them into
 * one module would mount the wrong page in whichever shell composed it.
 *
 * `group` says which shared nav section each item joins; the shell owns the
 * sections and their order. `requiresPermission` is a plain string — Tier 1
 * checks membership without knowing what the permission protects.
 */
export const educationOperatorModule: VerticalModule = {
  code: 'school',
  defaultContext: SCHOOL_DEFAULT_CONTEXT,
  entityIcon: AcademicCapIcon,

  // Two workspace sections, ordered as a school's week runs: the daily
  // register first, preparation tools second. Labels are the words a
  // teacher or bursar uses, not the names of our services.
  navItems: [
    { to: '/classroom', label: 'My classes', icon: AcademicCapIcon, group: 'workspace', section: 'School day', requiresFeature: OPERATIONS, requiresPermission: 'results:view', keywords: ['classroom', 'class', 'room', 'stream'] },
    { to: '/attendance', label: 'Attendance', icon: CalendarDaysIcon, group: 'workspace', section: 'School day', requiresFeature: OPERATIONS, requiresPermission: 'attendance:view', keywords: ['roll call', 'register', 'present', 'absent'] },
    { to: '/timetable', label: 'Timetable', icon: CalendarDaysIcon, group: 'workspace', section: 'School day', requiresFeature: OPERATIONS, requiresPermission: 'timetable:view', keywords: ['schedule', 'lessons', 'periods'] },
    { to: '/assignments', label: 'Homework', icon: BookOpenIcon, group: 'workspace', section: 'School day', requiresFeature: OPERATIONS, requiresPermission: 'assignments:view', keywords: ['assignments', 'tasks'] },
    { to: '/results', label: 'Exam results', icon: DocumentChartBarIcon, group: 'workspace', section: 'School day', requiresFeature: OPERATIONS, requiresPermission: 'results:view', keywords: ['grades', 'marks', 'exams', 'scores', 'report cards'] },
    { to: '/discipline', label: 'Behaviour', icon: HandRaisedIcon, group: 'workspace', section: 'School day', requiresFeature: OPERATIONS, requiresPermission: 'discipline:view', keywords: ['discipline', 'incidents', 'conduct'] },
    { to: '/planning', label: 'Lesson plans', icon: ClipboardDocumentListIcon, group: 'workspace', section: 'Teaching tools', requiresFeature: CBC_SUITE, requiresPermission: 'timetable:view', keywords: ['planning', 'scheme of work', 'copilot'] },
    { to: '/curriculum', label: 'CBC curriculum', icon: BookOpenIcon, group: 'workspace', section: 'Teaching tools', requiresFeature: CBC_SUITE, requiresPermission: 'timetable:view', keywords: ['curriculum', 'syllabus', 'strands', 'outcomes'] },
    { to: '/assessment', label: 'Question bank', icon: ClipboardDocumentCheckIcon, group: 'workspace', section: 'Teaching tools', requiresFeature: CBC_SUITE, requiresPermission: 'timetable:manage', keywords: ['assessment', 'questions', 'rubric', 'exam'] },
    { to: '/knec-alignment', label: 'KNEC exam readiness', icon: ChartBarSquareIcon, group: 'workspace', section: 'Teaching tools', requiresFeature: CBC_SUITE, requiresPermission: 'timetable:manage', keywords: ['knec', 'alignment', 'gap analysis', 'past papers'] },
    { to: '/fee-structures', label: 'Fee structures', icon: TableCellsIcon, group: 'finance', requiresFeature: COLLECTIONS, requiresPermission: 'fee_structures:view', keywords: ['fees', 'tuition', 'pricing'] },
    { to: '/announcements', label: 'Notices to parents', icon: MegaphoneIcon, group: 'engagement', requiresPermission: 'broadcasts:view', keywords: ['announcements', 'notices', 'alerts'] },
  ],

  labels: {
    entity: 'Student',
    payer: 'Parent',
    group: 'Family',
    obligation: 'Fee',
    period: 'Term',
    entity_reference: 'Admission number',
    institution: 'School',
    institution_detail: 'School details',
  },
  operatorLogin: {
    portalName: 'School Portal',
    tagline: 'Student records, school fees and parent access in one connected workspace.',
    points: [
      { title: 'Clear accounts for every family', description: 'Parents see fee balances and download receipts for confirmed payments.' },
      { title: 'Tools for each school role', description: 'Give finance teams, teachers and school leaders access to the work they manage.' },
      { title: 'Know what needs attention', description: 'Review collections, balances and payment plans without rebuilding spreadsheets.' },
    ],
    emailExample: 'you@school.ac.ke',
  },
  sharedNavLabels: {
  },

  settingsTabs: [{ id: 'classes', label: 'Classes & streams', slot: 'settingsStructurePanel' }],

  chargeCategories: [
    { code: 'tuition', label: 'Tuition' },
    { code: 'boarding', label: 'Boarding' },
    { code: 'transport', label: 'Transport' },
    { code: 'activity', label: 'Activity or trip' },
    { code: 'uniform', label: 'Uniform' },
    { code: 'books', label: 'Books' },
    { code: 'exam', label: 'Exam fee' },
    { code: 'other', label: 'Something else' },
  ],

  // A bursar asks, the principal approves (roles keep the shell's school words).
  waiverReasons: {
    in_kind: {
      label: 'Paid in kind',
      hint: 'Food, firewood, labour or other goods instead of money.',
      evidenceHint: 'The delivery note or store record, signed by whoever received it.',
      detailsLabel: 'What was received, how much, and what it was worth',
    },
    concession: {
      label: 'Bursary or sponsorship',
      hint: 'A bursary, sponsor or board decision reduces what is owed.',
      evidenceHint: 'The award letter or the board minutes.',
    },
  },

  routes: [
    { path: 'results', element: <ResultsPage />, requiresFeature: OPERATIONS, requiresPermission: 'results:view' },
    { path: 'attendance', element: <AttendancePage />, requiresFeature: OPERATIONS, requiresPermission: 'attendance:view' },
    // Shows exam results, so it needs what the results API needs.
    { path: 'classroom', element: <ClassroomDashboardPage />, requiresFeature: OPERATIONS, requiresPermission: 'results:view' },
    { path: 'planning', element: <PlanningPage />, requiresFeature: CBC_SUITE, requiresPermission: 'timetable:view' },
    { path: 'planning/history', element: <GenerationHistoryPage />, requiresFeature: CBC_SUITE, requiresPermission: 'timetable:view' },
    { path: 'curriculum', element: <CurriculumBrowserPage />, requiresFeature: CBC_SUITE, requiresPermission: 'timetable:view' },
    { path: 'assessment', element: <AssessmentPage />, requiresFeature: CBC_SUITE, requiresPermission: 'timetable:manage' },
    { path: 'knec-alignment', element: <KnecAlignmentPage />, requiresFeature: CBC_SUITE, requiresPermission: 'timetable:manage' },
    { path: 'assignments', element: <AssignmentsPage />, requiresFeature: OPERATIONS, requiresPermission: 'assignments:view' },
    { path: 'timetable', element: <TimetablePage />, requiresFeature: OPERATIONS, requiresPermission: 'timetable:view' },
    { path: 'discipline', element: <DisciplinePage />, requiresFeature: OPERATIONS, requiresPermission: 'discipline:view' },
    { path: 'announcements', element: <AnnouncementsPage />, requiresPermission: 'broadcasts:view' },
    { path: 'fee-structures', element: <FeeStructuresPage />, requiresFeature: COLLECTIONS, requiresPermission: 'fee_structures:view' },
  ],

  /**
   * Where shared operator pages have a school-shaped hole. Each slot is a
   * component the shell renders without knowing what a grade or a dorm is.
   */
  slots: {
    settingsStructurePanel: SchoolStructurePanel,
    inviteAssignmentFields: InviteAssignmentFields,
    cohortPicker: CohortPicker,
    cohortLabel: CohortLabel,
    entityAttributeFields: EntityAttributeFields,
    entityListFilter: EntityListFilter,
    setupChecklist: SetupChecklist,
    entityAttributeBadge: EntityAttributeBadge,
  },
}
